import { Popover } from '@headlessui/react'
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/outline";
import { useModal } from "../contexts";
import useUser from "../hooks/useUser";
import ProLabel from "./ProLabel";
import PayWallModalPro from "./PayWallModalPro";
import { useHistory } from 'react-router-dom';
import { Workflow } from '../interfaces';
import { useTranslation } from "react-i18next";

interface Props {
  value: string | null;
  setValue: (value: string | null, sendFromRunner: boolean) => void;
  workflow: Workflow;
  sendFromRunner: boolean;
  aliasDisplay: string | null;
  showAliases?: boolean;
  disabled?: boolean;
}

function EmailAliasPopover(props: Props) {
  const { user } = useUser();
  const history = useHistory();
  const { t } = useTranslation();

  const { openModal } = useModal();

  function openAliasesPage() {
    if (user?.subscription?.featureAliases)
      history.push("/settings/")
    else
      openModal(<PayWallModalPro />);
  }

  if (!user)
    return null;

  const isWorkflowOwner = props.workflow.owner?.id === user?.id;

  const defaultDisplay = (() => {
    if (!props.workflow.team)
      return `${user.fullName} <${user.email}>`;

    if (props.sendFromRunner)
      return `${t("Workflow Runner (Dynamic)")}`;

    const owner = props.workflow.owner;
    return `${t("Workflow Owner")}: ${owner?.fullName} <${owner?.email}>`;

  })();

  const otherDisplay = (() => {
    if (!props.sendFromRunner)
      return `${t("Workflow Runner (Dynamic)")}`;

    const owner = props.workflow.owner;
    return `${t("Workflow Owner")}: ${owner?.fullName} <${owner?.email}>`;
  })();

  const aliasOptions = [
    { id: null, display: defaultDisplay },
    ...(props.workflow.team ? [{ id: "__toggle", display: otherDisplay }] : []),
    ...(props.showAliases ? user.aliases.map(alias => ({ id: alias.id, display: `${t("Alias")}: ${alias.aliasName} <${alias.aliasEmail}>` })) : [])
  ]

  return (
    <Popover className="relative">
      <Popover.Button className='flex items-center gap-2 bg-gray-100 rounded px-2 text-gray-600 font-semibold'>
        {aliasOptions.find(alias => alias.id === props.value)?.display ?? props.aliasDisplay ?? defaultDisplay}
        {!props.disabled &&
          <ChevronDownIcon className='w-4 h-4' />
        }
      </Popover.Button>

      <Popover.Panel className="absolute -left-2 top-6 flex flex-col bg-white text-gray-600 shadow rounded text-sm z-40 min-w-[400px]">
        {!props.disabled &&
          <>
            {aliasOptions.filter(alias => alias.id !== props.value).map(alias =>
              <Popover.Button key={alias.id} className='font-gilroy font-semibold p-2 hover:bg-gray-50 cursor-pointer whitespace-nowrap text-left' onClick={() => alias.id === "__toggle" ? props.setValue(null, !props.sendFromRunner) : props.setValue(alias.id, props.sendFromRunner)}>
                {alias.display}
              </Popover.Button>
            )}
            {(props.showAliases && isWorkflowOwner) &&
              <div className='flex items-center gap-2 font-gilroy font-semibold p-2 text-blue hover:bg-gray-50 cursor-pointer' onClick={openAliasesPage}>
                <PlusIcon className='w-4 h-4' /> {t("Add Alias")}
                <ProLabel />
              </div>
            }
          </>
        }
        {!!props.workflow.team && props.showAliases &&
          <div className='px-2 py-1 text-xs text-gray-400 font-semibold'>
            {t("When an alias is selected, emails will always be sent from the Gmail account of the workflow owner.")}
          </div>
        }
      </Popover.Panel>
    </Popover>
  );
}

export default EmailAliasPopover;
